import { useTimeBlocks } from '@hooks/useTimeBlocks'
import { FC } from 'react'
import { Spinner } from 'components/loaders'
import { useTranslation } from 'react-i18next'
import { TimeBlock } from './TimeBlock'
import { twMerge } from 'tailwind-merge'
import { useDateTimeWithLocale } from '@hooks/useDateTimeWithLocale'

interface Props {
  timeCardId?: number
  timeZone?: string
}

const TimeBlocksContent: FC<Props> = ({ timeCardId, timeZone }) => {
  const { t } = useTranslation()
  const { data: timeBlocks, isLoading } = useTimeBlocks(timeCardId)

  if (isLoading && timeCardId) {
    return (
      <div className="flex flex-row items-center justify-center pt-4">
        <Spinner />
      </div>
    )
  }

  if (!timeBlocks?.length) {
    return (
      <span className="font-medium text-neutral-500 text-xs">
        {t('features.timeLogging.thereAreNoTimeBlocksForThisTimeCard')}
      </span>
    )
  }

  return (
    <div className="flex flex-col space-y-5">
      {timeBlocks
        .sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis())
        .map((block) => (
          <TimeBlock
            key={`${block.startTime.toMillis()}-${block.endTime.toMillis()}`}
            startTime={block.startTime}
            endTime={block.endTime}
            taskName={block.entry.task.name}
            projectName={block.entry.task.project.name}
            projectIntegrationCode={block.entry.task.projectIntegration?.code}
            displayPath={block.entry.task.displayPath}
            timeZone={timeZone}
          />
        ))}
    </div>
  )
}

export const TimeBlocksTab: FC<
  { className?: string; date: DateTime } & Props
> = ({ className, date, ...props }) => {
  const withLocale = useDateTimeWithLocale()

  return (
    <div className={twMerge('flex flex-col', className)}>
      <h2 className="text-neutral-900 font-medium mb-2">
        {withLocale(date).toLocaleString({
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })}
      </h2>
      <TimeBlocksContent {...props} />
    </div>
  )
}
